/**
 * GenerationForm V2
 *
 * Formular für die Bildgenerierung mit:
 * - File Upload mit Preview
 * - Preset-Auswahl (Standard + Custom Presets für PRO)
 * - Ratio-Auswahl
 * - Zustellung als Dropdown
 * - Experteneinstellungen (zusammenklappbar)
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Upload,
  X,
  Wand2,
  ChevronDown,
  ChevronUp,
  Settings,
  Coins,
  AlertTriangle,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { cn, formatCredits } from '@/lib/utils';
import { generateImage } from '@/lib/api/generation';
import { useWorkspaceConfig } from '@/hooks/useWorkspaceConfig';
import {
  Button,
  Input,
  Select,
  Textarea,
  Alert,
  AlertDescription,
  Badge,
} from '@/components/v2/ui';
import type { GenerateImageRequest } from '@/types';

// ===========================================
// CONSTANTS
// ===========================================

const STANDARD_PRESETS = [
  { value: 'Packshot', label: 'Packshot' },
  { value: 'Lifestyle', label: 'Lifestyle' },
  { value: 'Studio', label: 'Studio' },
];

const RATIOS = [
  { value: '1:1', label: '1:1 (Quadrat)' },
  { value: '4:3', label: '4:3 (Landschaft)' },
  { value: '3:4', label: '3:4 (Portrait)' },
  { value: '16:9', label: '16:9 (Breitbild)' },
  { value: '9:16', label: '9:16 (Story)' },
];

const DELIVERY_METHODS = [
  { value: 'wait', label: 'Warten (Direkt anzeigen)' },
  { value: 'mail', label: 'Per E-Mail senden' },
];

const IMAGE_FORMATS = [
  { value: 'png', label: 'PNG (Verlustfrei)' },
  { value: 'jpeg', label: 'JPEG (Komprimiert)' },
  { value: 'webp', label: 'WebP (Modern)' },
];

const PNG_BACKGROUNDS = [
  { value: 'white', label: 'Weiß' },
  { value: 'transparent', label: 'Transparent (KI entfernt Hintergrund)' },
];

const PNG_QUALITIES = [
  { value: 'very_high', label: 'Keine Kompression' },
  { value: 'web_optimized', label: 'Web optimiert' },
  { value: 'compact', label: 'Mittel' },
  { value: 'minimal', label: 'Niedrig' },
];

// ===========================================
// TYPES
// ===========================================

interface FormData {
  prompt_input: string;
  ratio: string;
  preset_type: string;
  delivery_method: 'wait' | 'mail';
  image_format: string;
  filename_prefix: string;
  filename_suffix: string;
  png_background: string;
  png_quality: string;
}

interface GenerationFormProps {
  onGenerationStart?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  className?: string;
}

interface ProductContext {
  id: string;
  name: string;
  sku?: string;
  gtin?: string;
  manufacturer?: string;
  sourceImageRole?: string;
}

// ===========================================
// KONFETTI EFFEKT
// ===========================================

function triggerConfetti() {
  const container = document.createElement('div');
  container.className = 'confetti-container';
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 9999;
    overflow: hidden;
  `;
  document.body.appendChild(container);

  const colors = ['#FF6B00', '#FF8C00', '#FFA266', '#22c55e', '#FFC299', '#ffffff'];
  const particleCount = 50;

  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('div');
    const color = colors[Math.floor(Math.random() * colors.length)];
    const x = Math.random() * 100;
    const delay = Math.random() * 0.5;
    const duration = 2 + Math.random() * 2;

    particle.style.cssText = `
      position: absolute;
      top: -10px;
      left: ${x}vw;
      width: 10px;
      height: 10px;
      background: ${color};
      border-radius: 2px;
      animation: confetti-fall ${duration}s ease-out ${delay}s forwards;
    `;
    container.appendChild(particle);
  }

  // CSS Animation hinzufügen
  if (!document.getElementById('confetti-style')) {
    const style = document.createElement('style');
    style.id = 'confetti-style';
    style.textContent = `
      @keyframes confetti-fall {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  setTimeout(() => container.remove(), 4000);
}

// ===========================================
// HELPER
// ===========================================

async function loadImageViaProxy(imageUrl: string): Promise<Blob> {
  const proxyUrl = `/api/v1/images/proxy?url=${encodeURIComponent(imageUrl)}`;
  const response = await fetch(proxyUrl, { credentials: 'include' });
  if (!response.ok) throw new Error(`Proxy-Fehler: ${response.status}`);
  return response.blob();
}

// ===========================================
// COMPONENT
// ===========================================

export function GenerationForm({
  onGenerationStart,
  onSuccess,
  onError,
  className,
}: GenerationFormProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [showExpertSettings, setShowExpertSettings] = useState(false);
  const [productImageLoading, setProductImageLoading] = useState(false);
  const [productInfo, setProductInfo] = useState<{ id: string; name: string } | null>(null);
  const [productContext, setProductContext] = useState<ProductContext | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { config, isLoading: configLoading } = useWorkspaceConfig();

  // ===========================================
  // PRODUKTBILD VON URL LADEN
  // ===========================================

  useEffect(() => {
    const productImageUrl = searchParams.get('productImageUrl');
    const productId = searchParams.get('productId');
    const productName = searchParams.get('productName');
    const productSku = searchParams.get('productSku');
    const productGtin = searchParams.get('productGtin');
    const productManufacturer = searchParams.get('productManufacturer');
    const sourceImageRole = searchParams.get('sourceImageRole');

    if (productImageUrl && productId) {
      setProductContext({
        id: productId,
        name: productName || '',
        sku: productSku || undefined,
        gtin: productGtin || undefined,
        manufacturer: productManufacturer || undefined,
        sourceImageRole: sourceImageRole || undefined,
      });

      loadProductImage(productImageUrl, productId, productName || '');

      // URL-Parameter entfernen
      const newParams = new URLSearchParams(searchParams);
      ['productImageUrl', 'productId', 'productName', 'productSku', 'productGtin', 'productManufacturer', 'sourceImageRole'].forEach(p => newParams.delete(p));
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadProductImage = async (imageUrl: string, productId: string, productName: string) => {
    setProductImageLoading(true);
    setProductInfo({ id: productId, name: productName });

    try {
      const blob = await loadImageViaProxy(imageUrl);
      const contentType = blob.type || 'image/png';
      const extension = contentType.split('/')[1] || 'png';
      const filename = `product_${productId}.${extension}`;
      const file = new File([blob], filename, { type: contentType });

      setSelectedFile(file);
      setPreview(imageUrl);
      toast.success(`Produktbild "${productName || productId}" geladen`);
    } catch (err) {
      console.error('Error loading product image:', err);
      toast.error('Produktbild konnte nicht geladen werden');
      setProductInfo(null);
      setProductContext(null);
    } finally {
      setProductImageLoading(false);
    }
  };

  // ===========================================
  // AUTO-TAGS
  // ===========================================

  const collectAutoTags = (preset: string, ratio: string): string[] => {
    const tags: string[] = [];
    if (preset) tags.push(preset);
    if (ratio) tags.push(ratio);
    if (productContext) {
      if (productContext.sku) tags.push(productContext.sku);
      if (productContext.gtin) tags.push(productContext.gtin);
      if (productContext.manufacturer) tags.push(productContext.manufacturer);
      if (productContext.sourceImageRole) tags.push(productContext.sourceImageRole);
    }
    return tags;
  };

  // ===========================================
  // FORM
  // ===========================================

  const { register, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      ratio: '1:1',
      preset_type: 'Packshot',
      delivery_method: 'wait',
      prompt_input: '',
      image_format: 'png',
      filename_prefix: '',
      filename_suffix: '',
      png_background: 'white',
      png_quality: 'web_optimized',
    },
  });

  // Defaults aus Config laden
  useEffect(() => {
    if (config?.defaults) {
      setValue('ratio', config.defaults.ratio || '1:1');
      setValue('preset_type', config.defaults.preset || 'Packshot');
      setValue('delivery_method', config.defaults.delivery_method || 'wait');
      setValue('image_format', config.defaults.image_format || 'png');
      setValue('filename_prefix', config.defaults.filename_prefix || '');
      setValue('filename_suffix', config.defaults.filename_suffix || '');
      setValue('png_background', config.defaults.png_background || 'transparent');
      setValue('png_quality', String(config.defaults.png_quality || 70));
    }
  }, [config, setValue]);

  const imageFormat = watch('image_format');

  // ===========================================
  // MUTATION
  // ===========================================

  const mutation = useMutation({
    mutationFn: (data: GenerateImageRequest) => generateImage(data),
    onSuccess: (response) => {
      toast.success(response.message || 'Bild erfolgreich generiert!');
      triggerConfetti();

      setSelectedFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      queryClient.invalidateQueries({ queryKey: ['workspace', 'images'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'config'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Fehler bei der Bildgenerierung');
      onError?.();
    },
  });

  // ===========================================
  // HANDLERS
  // ===========================================

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Nur PNG, JPG und WebP werden unterstützt');
      return;
    }

    if (file.size > 16 * 1024 * 1024) {
      toast.error('Maximale Dateigröße: 16 MB');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        handleFileSelect({ target: { files: dataTransfer.files } } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const onSubmit = (formData: FormData) => {
    if (!selectedFile) {
      toast.error('Bitte wählen Sie ein Bild aus');
      return;
    }

    if (config && config.user_credits < config.cost) {
      toast.error(`Nicht genügend Guthaben. Benötigt: ${formatCredits(config.cost)}`);
      return;
    }

    const presetType = formData.preset_type;
    let customPresetId: string | undefined;

    // Custom Preset: UUID extrahieren für custom_instruction_id
    // presetType bleibt 'custom:<uuid>' - Backend erwartet dieses Format
    if (formData.preset_type.startsWith('custom:')) {
      customPresetId = formData.preset_type.replace('custom:', '');
    }

    onGenerationStart?.();

    const autoTags = collectAutoTags(presetType, formData.ratio);

    mutation.mutate({
      image: selectedFile,
      prompt_input: formData.prompt_input,
      ratio: formData.ratio,
      preset_type: presetType,
      delivery_method: formData.delivery_method,
      image_format: formData.image_format,
      filename_prefix: formData.filename_prefix,
      filename_suffix: formData.filename_suffix,
      png_background: formData.png_background,
      png_quality: formData.png_quality,
      custom_preset_id: customPresetId,
      custom_instruction_id: customPresetId,
      auto_tags: autoTags,
    });
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setProductInfo(null);
    setProductContext(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ===========================================
  // RENDER
  // ===========================================

  const isSubmitting = mutation.isPending;
  const cost = config?.cost || 0;
  const credits = config?.user_credits || 0;
  const hasEnoughCredits = credits >= cost;
  const customPresets = config?.custom_presets || config?.custom_instructions || [];
  const canUseCustomPresets = config?.can_use_custom_presets ?? config?.can_use_custom_instructions ?? false;

  // Build preset options
  const presetOptions = [
    ...STANDARD_PRESETS.map(p => ({ value: p.value, label: p.label })),
    ...(canUseCustomPresets && customPresets.length > 0
      ? customPresets.map(p => ({ value: `custom:${p.id}`, label: `${p.name} (Custom)` }))
      : []),
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col h-full', className)}>
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Credits Display */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-100 dark:bg-neutral-800">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Guthaben:
            </span>
            <Badge variant={hasEnoughCredits ? 'success' : 'error'}>
              {formatCredits(credits)}
            </Badge>
          </div>
          <span className="text-xs text-neutral-500">
            Kosten: {formatCredits(cost)}
          </span>
        </div>

        {!hasEnoughCredits && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Nicht genügend Guthaben!{' '}
              <a href="/v2/credits" className="underline font-medium">
                Aufladen
              </a>
            </AlertDescription>
          </Alert>
        )}

        {/* File Upload */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Produktbild
          </label>

          {productImageLoading ? (
            <div className="flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-700">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500 mb-2" />
              <p className="text-sm text-neutral-500">Lade Produktbild...</p>
            </div>
          ) : preview ? (
            <div className="relative rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
              <img src={preview} alt="Preview" className="w-full h-48 object-contain" />
              <button
                type="button"
                onClick={clearFile}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                aria-label="Bild entfernen"
              >
                <X className="h-4 w-4" />
              </button>
              {productInfo && (
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/70 text-white text-xs">
                  {productInfo.name || `Produkt ${productInfo.id.slice(0, 8)}`}
                </div>
              )}
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                isDragOver
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
                  : 'border-neutral-300 dark:border-neutral-700 hover:border-primary-400'
              )}
            >
              <Upload className="h-8 w-8 text-neutral-400 mb-2" />
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                Klicken oder Bild hierher ziehen
              </p>
              <p className="text-xs text-neutral-400">PNG, JPG, WebP (max. 16 MB)</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Ratio & Preset */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Verhältnis"
            options={RATIOS}
            value={watch('ratio')}
            onValueChange={(v) => setValue('ratio', v)}
          />
          <Select
            label="Preset"
            options={presetOptions}
            value={watch('preset_type')}
            onValueChange={(v) => setValue('preset_type', v)}
          />
        </div>

        {/* Prompt */}
        <Textarea
          label="Zusätzliche Anweisungen"
          placeholder="z.B. 'auf weißem Hintergrund' oder 'mit Schattenwurf'"
          rows={2}
          {...register('prompt_input')}
          helperText="Bei Custom Presets wird [Prompt] ersetzt"
        />

        {/* Delivery Method */}
        <Select
          label="Zustellung"
          options={DELIVERY_METHODS}
          value={watch('delivery_method')}
          onValueChange={(v) => setValue('delivery_method', v as 'wait' | 'mail')}
        />

        {/* Expert Settings */}
        <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowExpertSettings(!showExpertSettings)}
            className="w-full flex items-center justify-between p-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Experteneinstellungen
            </span>
            {showExpertSettings ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {showExpertSettings && (
            <div className="p-3 pt-0 space-y-3 border-t border-neutral-200 dark:border-neutral-700">
              <Select
                label="Bildformat"
                options={IMAGE_FORMATS}
                value={watch('image_format')}
                onValueChange={(v) => setValue('image_format', v)}
                helperText="PNG ist verlustfrei, JPEG/WebP sind komprimierter"
              />

              <Input
                label="Dateiname-Prefix"
                placeholder="z.B. 'img_', 'product_'"
                {...register('filename_prefix')}
                helperText="Wird vor dem Dateinamen eingefügt"
              />

              <Input
                label="Dateiname-Suffix"
                placeholder="z.B. '_final', '_v1'"
                {...register('filename_suffix')}
                helperText="Wird nach der UUID eingefügt"
              />

              {/* PNG Options */}
              {imageFormat === 'png' && (
                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700 space-y-3">
                  <p className="text-xs text-neutral-500">PNG-spezifische Optionen</p>

                  <Select
                    label="Hintergrundfarbe"
                    options={PNG_BACKGROUNDS}
                    value={watch('png_background')}
                    onValueChange={(v) => setValue('png_background', v)}
                    helperText="Transparent entfernt den Hintergrund mit KI"
                  />

                  <Select
                    label="Bildqualität"
                    options={PNG_QUALITIES}
                    value={watch('png_quality')}
                    onValueChange={(v) => setValue('png_quality', v)}
                    helperText="Web optimiert bietet die beste Balance"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 space-y-2">
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={isSubmitting || !selectedFile || !hasEnoughCredits || configLoading}
          loading={isSubmitting}
        >
          {isSubmitting ? (
            'Generiere...'
          ) : (
            <>
              <Wand2 className="h-4 w-4 mr-2" />
              Bild generieren
            </>
          )}
        </Button>

        {canUseCustomPresets && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => window.location.href = '/v2/custom-presets'}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Custom Presets verwalten
          </Button>
        )}
      </div>
    </form>
  );
}

export default GenerationForm;
